import { useState, useCallback, useRef } from 'react';

export const useDragAndDrop = (onNodeMove, gridSize = 20) => {
  const [dragState, setDragState] = useState({
    isDragging: false,
    nodeId: null,
    startX: 0,
    startY: 0,
    offsetX: 0,
    offsetY: 0
  });

  const dragRef = useRef(dragState);
  dragRef.current = dragState;

  // Snap to grid helper
  const snapToGrid = useCallback((value) => {
    return Math.round(value / gridSize) * gridSize;
  }, [gridSize]);

  // Start dragging a node
  const startDrag = useCallback((e, nodeId, nodeX, nodeY) => {
    e.stopPropagation();

    const clientX = e.clientX || (e.touches && e.touches[0].clientX);
    const clientY = e.clientY || (e.touches && e.touches[0].clientY);

    setDragState({
      isDragging: true,
      nodeId,
      startX: clientX,
      startY: clientY,
      offsetX: clientX - nodeX,
      offsetY: clientY - nodeY
    });
  }, []);

  // Handle drag movement
  const onDrag = useCallback((e, zoom = 1) => {
    if (!dragRef.current.isDragging) return;

    const clientX = e.clientX || (e.touches && e.touches[0].clientX);
    const clientY = e.clientY || (e.touches && e.touches[0].clientY);

    // Calculate new position with zoom compensation
    const newX = snapToGrid((clientX - dragRef.current.offsetX) / zoom);
    const newY = snapToGrid((clientY - dragRef.current.offsetY) / zoom);

    if (onNodeMove) {
      // Allow any position for infinite canvas
      onNodeMove(dragRef.current.nodeId, {
        x: newX,
        y: newY
      });
    }
  }, [snapToGrid, onNodeMove]);

  // End dragging
  const endDrag = useCallback(() => {
    setDragState({
      isDragging: false,
      nodeId: null,
      startX: 0,
      startY: 0,
      offsetX: 0,
      offsetY: 0
    });
  }, []);

  return {
    isDragging: dragState.isDragging,
    draggedNodeId: dragState.nodeId,
    startDrag,
    onDrag,
    endDrag,
    snapToGrid
  };
};

export const usePanning = () => {
  const [panState, setPanState] = useState({
    isPanning: false,
    startX: 0,
    startY: 0,
    startPanX: 0,
    startPanY: 0
  });

  const panRef = useRef(panState);
  panRef.current = panState;

  const startPan = useCallback((e, currentPanX, currentPanY) => {
    const clientX = e.clientX || (e.touches && e.touches[0].clientX);
    const clientY = e.clientY || (e.touches && e.touches[0].clientY);

    setPanState({
      isPanning: true,
      startX: clientX,
      startY: clientY,
      startPanX: currentPanX,
      startPanY: currentPanY
    });
  }, []);

  const onPan = useCallback((e) => {
    if (!panRef.current.isPanning) return null;

    const clientX = e.clientX || (e.touches && e.touches[0].clientX);
    const clientY = e.clientY || (e.touches && e.touches[0].clientY);

    const deltaX = clientX - panRef.current.startX;
    const deltaY = clientY - panRef.current.startY;

    return {
      panX: panRef.current.startPanX + deltaX,
      panY: panRef.current.startPanY + deltaY
    };
  }, []);

  const endPan = useCallback(() => {
    setPanState({
      isPanning: false,
      startX: 0,
      startY: 0,
      startPanX: 0,
      startPanY: 0
    });
  }, []);

  return {
    isPanning: panState.isPanning,
    startPan,
    onPan,
    endPan
  };
};

export default useDragAndDrop;
